import { Injectable, Injector, OnDestroy } from "@angular/core";
import { BehaviorSubject, finalize, Subscription, take, tap } from "rxjs";
import { API_HOST, settings } from "environments/environment";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { ApiService } from "./api.service";
import { SettingsService } from "./settings.service";
import { PanelService } from "./panel.service";
import { DialogService } from "./dialog.service";
import { SynchronizationService } from "./synchronization.service";
import { DbService } from "./db.service";
import { ToastService } from "./toast.service";
import { APIResponse } from "fakturnia-shared";
import { NotificationData } from "fakturnia-shared";
import { UserSettings } from "fakturnia-shared";
import { DeviceDetectorService } from 'ngx-device-detector';
import { SnackbarService } from "./snackbar.service";
import { SubscriptionService } from "./subscription.service";
import { MatDialog } from "@angular/material/dialog";
import { TwoFactorDialogComponent, TwoFactorDialogResult } from "shared/dialogs/two-factor-dialog/two-factor-dialog.component";
import Dexie from "dexie";

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {

  private _subscriptions: Subscription[] = []
  private _isLoggedIn$ = new BehaviorSubject<boolean>(false);
  public isLoggedIn$ = this._isLoggedIn$.asObservable();


  public readonly ACCESS_TOKEN = settings.accessTokenKey;
  public readonly SESSION_ID = settings.sessionIdKey;
  public readonly REFRESH_TOKEN = settings.refreshTokenKey;

  private readonly _deviceInfo = this._deviceService.getDeviceInfo()
  private readonly _browser = `${this._deviceInfo.browser} v${this._deviceInfo.browser_version}`;
  private readonly _deviceName = `${this._deviceInfo.os} ${this._deviceService.getDeviceInfo().os_version}`;
  private readonly _platform = this._deviceService.deviceType;

  constructor(
    private _router: Router,
    private _http: HttpClient,
    private _dbService: DbService,
    private _apiService: ApiService,
    private _settingsService: SettingsService,
    private _panelService: PanelService,
    private _dialogService: DialogService,
    private _synchronizationService: SynchronizationService,
    private _toastService: ToastService,
    private _deviceService: DeviceDetectorService,
    private _snackbarService: SnackbarService,
    private _subscriptionService: SubscriptionService,
    private _dialog: MatDialog,
    private _injector: Injector
  ) {
    this._isLoggedIn$.next(!!this.accessToken);

    this._subscriptions.push(this.isLoggedIn$.subscribe({
      next: (data) => {
        if (data == false) {
          this._synchronizationService.resetLastSync()
        }
      }
    }))
  }

  get accessToken() {
    return localStorage.getItem(this.ACCESS_TOKEN);
  }

  get sessionId() {
    return localStorage.getItem(this.SESSION_ID);
  }

  get refreshToken() {
    return localStorage.getItem(this.REFRESH_TOKEN);
  }

  isLoggedIn() {
    return this._isLoggedIn$.value;
  }

  loginOrRegister(action: string, emailAddress: string | any, password: string | any, redirect: boolean = true) {

    const toReturn = new BehaviorSubject<any>(null)
    this._http.post(API_HOST + `auth/${action}`, {
      emailAddress,
      password,
      browser: this._browser,
      deviceName: this._deviceName,
      platform: this._platform,
      registerSource: action === 'register' ? 'WEB' : undefined
    })
      .pipe(take(1))
      .pipe(finalize(() => {
        toReturn.next(true)
      }))
      .subscribe({
        next: (response: APIResponse) => {

          if (response.success == false && action === 'login') {
            this._snackbarService.showSnackBar("Adres e-mail lub hasło jest nieprawidłowe.", "warning")
            return
          }

          if (response.success == false && action === 'register') {
            this._toastService.warning(response.message)
            return
          }

          if (response.success == true && action === 'login') {

            // Sprawdź czy wymaga 2FA
            if (response.data.requiresTwoFactor) {
              this._handleTwoFactorLogin(response.data, redirect);
              return;
            }

            this._completeLogin(response.data, redirect);
          }

          else if (response.success == true && action === 'register') {
            this._toastService.showToast(new NotificationData({
              title: 'Sukces',
              status: 'success',
              message: 'Konto zostało utworzone.',
            }))

            this._pushToDataLayer({
              "event":  "conversion_event_signup_1"
            })
            this._uet_report_conversion()
            
            this.loginOrRegister('login', emailAddress, password, redirect)
          }
        }
      }
      )

    return toReturn.asObservable()
  }

  getRefreshToken() {
    return this._http.post(API_HOST + 'auth/refresh-token', { refreshToken: this.refreshToken }).pipe(
      tap(
        (response: APIResponse) => {
          localStorage.setItem(this.ACCESS_TOKEN, response.data.accessToken);
          localStorage.setItem(this.REFRESH_TOKEN, response.data.refreshToken);
          localStorage.setItem(this.SESSION_ID, response.data.sessionId);
        }
      )
    );
  }

  changePassword(data) {
    return this._http.post(API_HOST + 'auth/change-password', { currentPassword: data.currentPassword, newPassword: data.newPassword });
  }
  changeEmailAddress(data) {
    return this._http.post(API_HOST + 'auth/change-email-address', { password: data.password, emailAddress: data.emailAddress });
  }
  restoreAccount(data) {
    return this._http.post(API_HOST + 'auth/restore-account', data);
  }
  setNewPassword(data) {
    return this._http.post(API_HOST + 'auth/set-new-password', data);
  }

  /**
   * Czyści wszystkie cache w pamięci serwisów trzymających dane usera (Products, Clients, Documents,
   * ActivityLogs, KsefInvoices, Notifications). Wywoływane przy logout / switch konta, żeby uniknąć
   * pokazywania danych poprzedniego konta zanim skończy się synchronizacja nowego.
   *
   * Lazy import + Injector — unikamy circular DI z serwisami, które importują AuthService.
   */
  private async _clearDataCaches(): Promise<void> {
    try {
      const [
        { ProductsService },
        { ClientsService },
        { DocumentsService },
        { ActivityLogsService },
        { KsefInvoicesService },
        { NotificationsService }
      ] = await Promise.all([
        import('./products.service'),
        import('./clients.service'),
        import('./documents.service'),
        import('./activity-logs.service'),
        import('./ksef-invoices.service'),
        import('./notifications.service')
      ]);
      this._injector.get(ProductsService).clear();
      this._injector.get(ClientsService).clear();
      this._injector.get(DocumentsService).clear();
      this._injector.get(ActivityLogsService).clear();
      this._injector.get(KsefInvoicesService).clear();
      this._injector.get(NotificationsService).clear();
    } catch (e) {
      console.warn('[AuthService]: Nie udało się wyczyścić cache serwisów', e);
    }
  }

  logout({ muted = false }) {

    this._http.post(API_HOST + `auth/logout`, {})
      .pipe(
        finalize(() => {
          this._isLoggedIn$.next(false);
          localStorage.removeItem(this.ACCESS_TOKEN)
          localStorage.removeItem('role')
          // Wyczyść subskrypcję
          this._subscriptionService.clear();
          // Wyczyść cache w pamięci wszystkich serwisów z danymi usera
          this._clearDataCaches();
          localStorage.removeItem(this.REFRESH_TOKEN)

          if (muted == false) {
            this._snackbarService.showSnackBar('Zostałeś prawidłowo wylogowany z aplikacji.', 'info')
          }

          this._deleteDatabases()

          this._router.navigateByUrl('/logowanie');
          this._panelService.close();
        }))
      .pipe(take(1))
      .subscribe({
        next: (response: APIResponse) => {

        }
      })


  }

  /**
   * Clear local storage and set logged status as logged out
   * That method is not calling any logout action because session is broken and it could 
   * result not expected results such as errors
   */
  clearSession() {
    localStorage.clear()
    this._isLoggedIn$.next(false)
    // Wyczyść subskrypcję
    this._subscriptionService.clear();
    // Wyczyść cache w pamięci wszystkich serwisów z danymi usera
    this._clearDataCaches();
    this._deleteDatabases()
  }

  private async _deleteDatabases() {
    try {

      // Remove databases
      const dbs = await Dexie.getDatabaseNames()
      dbs.forEach(db => {
        const deleteRequest = indexedDB.deleteDatabase(db)

        deleteRequest.onsuccess = () => {
          console.log(`[AuthSignOutComponent]: Deleted application databases: ${db}`)
        }

        deleteRequest.onerror = (error) => {
          console.error(`[AuthSignOutComponent]: Error deleting database ${db}:`, error)
        }
      })

    } catch (ex: any) {
      console.log(`[AuthSignOutComponent]: ${ex.message}`);
    }
  }

  private _pushToDataLayer(eventData: any) {
    try {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push(eventData);
      console.log('Data Layer Event:', eventData);
    } catch(err:any){
			console.log("GTM dataLayer push failed");
			console.log(err.message);
		}
  }

  private _uet_report_conversion() {
    window.uetq = window.uetq || [];
    window.uetq.push('event', '', {"event_category":"signup"});
  }

  /**
   * Obsługuje logowanie wymagające weryfikacji dwuetapowej
   */
  private _handleTwoFactorLogin(data: any, redirect: boolean): void {
    const dialogRef = this._dialog.open(TwoFactorDialogComponent, {
      data: {
        tempToken: data.tempToken,
        method: data.method,
        maskedEmail: data.maskedEmail,
        deviceInfo: {
          browser: this._browser,
          deviceName: this._deviceName,
          platform: this._platform
        }
      },
      disableClose: true,
      width: '450px',
      panelClass: 'two-factor-dialog',
      autoFocus: false
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((result: TwoFactorDialogResult | null) => {
        if (result?.success && result.data) {
          this._completeLogin(result.data, redirect);
        } else {
          this._snackbarService.showSnackBar("Logowanie anulowane.", "info");
        }
      });
  }

  /**
   * Dokańcza proces logowania po pomyślnej weryfikacji
   */
  private _completeLogin(data: any, redirect: boolean): void {
    this.applyLoginData(data, { redirect, mode: 'login' });
  }

  /**
   * Dokańcza logowanie przez dostawcę social (Google/Apple) z poziomu komponentu callback.
   * Obsługuje 2FA tak samo jak logowanie hasłem — gdy backend zwróci `requiresTwoFactor`,
   * pokazujemy dialog weryfikacji zamiast próbować od razu utworzyć sesję.
   */
  public completeSocialLogin(data: any, redirect: boolean): void {
    if (data?.requiresTwoFactor) {
      this._handleTwoFactorLogin(data, redirect);
      return;
    }
    this._completeLogin(data, redirect);
  }

  /**
   * Stosuje dane logowania (z login / 2FA / switch konta) — zapisuje tokeny,
   * przełącza Dexie na bazę nowego usera, pobiera ustawienia i synchronizuje.
   *
   * @param data {id, accessToken, refreshToken, sessionId, role}
   * @param opts.mode 'login' = pełna ścieżka (toast, setup-company), 'switch' = ciche przełączenie
   * @param opts.redirect przekieruj na /konto po sukcesie
   */
  public applyLoginData(data: any, opts: { redirect?: boolean; mode?: 'login' | 'switch' } = {}): void {
    const mode = opts.mode || 'login';
    const redirect = opts.redirect !== false;

    if (typeof data?.id == 'undefined') {
      this.logout({ muted: true });
      return;
    }

    this._isLoggedIn$.next(true);

    localStorage.setItem(this.ACCESS_TOKEN, data.accessToken);
    localStorage.setItem(this.REFRESH_TOKEN, data.refreshToken);
    localStorage.setItem(this.SESSION_ID, data.sessionId);

    // Każde konto ma OSOBNĄ bazę Dexie (klucz = userId).
    // Najpierw czyścimy cache w pamięci (Products/Clients/Documents/...) żeby UI nie pokazywał
    // danych z poprzedniego konta zanim skończy się synchronizacja nowego.
    this._clearDataCaches();
    // Przełączamy bazę — poprzednia zostaje zachowana lokalnie (cache offline drugiego konta).
    this._dbService.setDatabase(data.id);

    this._apiService.getUserSettings().pipe(take(1)).subscribe({
      next: async (settingsResponse: APIResponse) => {

        if (settingsResponse.success == true) {
          await this._settingsService.updateUserSettings(<UserSettings>settingsResponse.data);

          // Zasil cache powiązanych kont od razu — backend zwraca je w user settings,
          // dzięki czemu UI (submenu "Przełącz konto") jest świeży zaraz po loginie/switchu.
          // Lazy injector — unikamy circular DI z ConnectedAccountsService.
          try {
            const { ConnectedAccountsService } = await import('./connected-accounts.service');
            const svc = this._injector.get(ConnectedAccountsService);
            svc.setAccounts(settingsResponse.data.connectedAccounts);
          } catch (e) {
            console.warn('[AuthService]: Nie udało się zasilić ConnectedAccountsService', e);
          }

          if (settingsResponse.data.subscription) {
            this._subscriptionService.setSubscription(settingsResponse.data.subscription);
          } else {
            this._subscriptionService.markAsLoaded();
          }

          // Setup company tylko przy pierwszym logowaniu — nie przy switch.
          if (
            mode === 'login' &&
            (settingsResponse.data.user.hasCompletedRegistration == false ||
              typeof settingsResponse.data.user.hasCompletedRegistration == 'undefined')
          ) {
            this._dialogService.showSetUpCompanyDialog().pipe(take(1)).subscribe();
          }
        } else {
          this._snackbarService.showSnackBar("Nie udało się pobrać ustawień konta.", "warning");
          return;
        }
      }
    });

    // Synchronizacja danych nowego konta (odświeży faktury, klientów, produkty, powiadomienia).
    this._synchronizationService.synchronize();

    if (mode === 'login') {
      this._snackbarService.showSnackBar('Witaj, życzymy przyjemnej pracy!', 'info');
    } else {
      this._snackbarService.showSnackBar(
        data.email ? `Przełączono na konto ${data.email}` : 'Przełączono na powiązane konto',
        'info'
      );
    }

    if (redirect) this._router.navigate(['konto']);
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe())
  }

}